import { createRouter, createWebHistory, RouterView } from 'vue-router';

import i18n from '@/i18n';
import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useDoorsaleStore } from '@/stores/doorsaleStore';
import { useGeneralStore } from '@/stores/general';
import { isEmbed } from '@/utils/isEmbed';
import { slugify } from '@/utils/slugify';
import { parseHashParams } from '@/utils/urlParams';
import CancelOrderView from '@/views/CancelOrderView.vue';
import CheckoutEventStateView from '@/views/CheckoutEventStateView.vue';
import CheckoutInformationView from '@/views/CheckoutInformationView.vue';
import CheckoutPaymentView from '@/views/CheckoutPaymentView.vue';
import CheckoutReceiptView from '@/views/CheckoutReceiptView.vue';
import CheckoutSeatSelectionView from '@/views/CheckoutSeatSelectionView.vue';
import CheckoutTimeoutView from '@/views/CheckoutTimeoutView.vue';
import CheckoutUpsaleView from '@/views/CheckoutUpsaleView.vue';
import ContactView from '@/views/ContactView.vue';
import GiftCardBalanceView from '@/views/GiftCardBalanceView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import ResendTicketsView from '@/views/ResendTicketsView.vue';
import SelfscanView from '@/views/SelfscanView.vue';
import TermsView from '@/views/TermsView.vue';
import { useWebsocketStore } from '@/websocket/websocket';

import CheckoutTicketsView from '../views/CheckoutTicketsView.vue';
import DoorsaleEventListView from '../views/doorsale/DoorsaleEventListView.vue';
import DoorsaleInformationView from '../views/doorsale/DoorsaleInformationView.vue';
import DoorsaleReceiptView from '../views/doorsale/DoorsaleReceiptView.vue';
import DoorsaleSeatsView from '../views/doorsale/DoorsaleSeatsView.vue';
import DoorsaleTicketsView from '../views/doorsale/DoorsaleTicketsView.vue';
import DoorsaleUpsaleView from '../views/doorsale/DoorsaleUpsaleView.vue';
import EventDetailView from '../views/EventDetailView.vue';
import EventListView from '../views/EventListView.vue';
import { legacyRedirectRoutes } from './legacyRedirects';

// NOTE see src/App.vue for dynamic route additions based on fetched data

// Prevent the browser's native scroll restoration from racing with Vue Router's scrollBehavior,
// which can cause incorrect layout measurements on reload (e.g. seatmap wrapperTop).
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (to.path.startsWith('/doorsale')) {
      return { top: 0 };
    }
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
  routes: [
    {
      path: '/',
      name: 'event-list',
      alias: '/home',
      component: EventListView,
    },
    {
      path: '/event/:id/:name?', 
      name: 'event-detail',
      component: EventDetailView,
    },
    {
      path: '/admin/previewevent',
      name: 'admin-preview-event',
      component: EventDetailView,
      props: { previewMode: true },
    },
    {
      path: '/:id(\\d+|[0-9a-f]{96})/:name?/tickets',
      name: 'checkout-tickets',
      component: CheckoutTicketsView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/upsale',
      name: 'checkout-upsale',
      component: CheckoutUpsaleView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/seats',
      name: 'checkout-seats',
      component: CheckoutSeatSelectionView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/information',
      name: 'checkout-information',
      component: CheckoutInformationView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/payment',
      name: 'checkout-payment',
      alias: '/paymentfailed',
      component: CheckoutPaymentView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/timeout',
      name: 'checkout-timeout',
      component: CheckoutTimeoutView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/receipt',
      name: 'checkout-receipt',
      alias: '/receipt',
      component: CheckoutReceiptView,
      meta: { embedAllowed: true },
    },
    {
      path: '/checkout/error',
      name: 'checkout-error',
      component: CheckoutEventStateView,
      props: {
        data: {
          id: 0,
          state: 'error',
          server_time: 0,
        },
      },
      meta: { embedAllowed: true },
    },
    {
      path: '/terms',
      name: 'terms',
      component: TermsView,
    },
    {
      path: '/contact',
      name: 'contact',
      component: ContactView,
    },
    {
      path: '/resend-tickets',
      name: 'resend-tickets',
      component: ResendTicketsView,
    },
    {
      path: '/selfscan',
      name: 'selfscan',
      component: SelfscanView,
    },
    {
      path: '/doorsale',
      component: RouterView,
      children: [
        { path: '',                    name: 'doorsale-events',      component: DoorsaleEventListView },
        { path: 'tickets/:eventId',    name: 'doorsale-tickets',     component: DoorsaleTicketsView },
        { path: 'upsale/:eventId',     name: 'doorsale-upsale',      component: DoorsaleUpsaleView },
        { path: 'seats/:eventId',      name: 'doorsale-seats',       component: DoorsaleSeatsView },
        { path: 'information',         name: 'doorsale-information', component: DoorsaleInformationView },
        { path: 'receipt',             name: 'doorsale-receipt',     component: DoorsaleReceiptView },
      ],
    },
    {
      path: '/cancel-order',
      name: 'cancel-order',
      component: CancelOrderView,
    },
    {
      path: '/get-gift-card-balance',
      name: 'gift-card-balance',
      component: GiftCardBalanceView,
    },
    {
      path: '/:lang(da|en)',
      component: EventListView,
      beforeEnter: (to) => {
        i18n.global.locale.value = to.params.lang as 'da' | 'en';
        return '/';
      },
    },
    ...legacyRedirectRoutes,
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
    },
  ],
});

router.afterEach((to, from) => {
  localStorage.setItem('lastVisitedRoute', from.fullPath);
});

router.beforeEach((to, from, next) => {
  if (isEmbed() && !to.meta.embedAllowed) {
    const checkoutStore = useCheckoutStore();
    const candidateId = to.params.id ?? to.query.id ?? checkoutStore.getCheckoutID();
    const rawId = Array.isArray(candidateId) ? candidateId[0] : candidateId?.toString();
    const validId = rawId && (/^\d+$/.test(rawId) || /^[0-9a-f]{96}$/.test(rawId));

    if (validId) {
      next({
        name: 'checkout-tickets',
        params: { id: rawId },
        query: to.query,
        replace: true,
      });
    } else {
      next({ name: 'checkout-error', replace: true });
    }
    return;
  }

  // Doorsale guard: runs on every navigation to a doorsale sub-route.
  // beforeEnter on the parent route only fires when first entering the /doorsale group,
  // not on child-to-child navigation — so this must live in beforeEach.
  if (to.name?.toString().startsWith('doorsale-')) {
    const doorsaleStore = useDoorsaleStore();
    if (!doorsaleStore.requireAdminSession()) return;
    // Clock-math expiry check — see doorsaleStore for full caveat on limitations
    if (doorsaleStore.isSessionExpired()) {
      doorsaleStore.handleSessionExpired();
      return;
    }
    if (!doorsaleStore.isActive) {
      doorsaleStore.init();
      // Clear only the clientID so a fresh one is fetched with the admin session
      // active (has_doorsale_permission = 1). We intentionally do NOT clear the
      // rtoken — the server updates doorsale_permission on existing rtokens when
      // they reconnect with a new clientID, so the basket survives page reloads.
      const ws = useWebsocketStore();
      ws.clearClientID();
      ws.open();
    }
    next();
    return;
  }

  // Legacy "#modal" links built on the current /event/:id/:name URL format should open ticket selection directly
  if (to.name === 'event-detail' && to.hash.startsWith('#modal')) {
    const hashParams = parseHashParams(to.hash);
    next({
      name: 'checkout-tickets',
      params: { id: to.params.id as string, name: to.params.name },
      query: { ...to.query, ...hashParams },
      hash: '',
    });
    return;
  }

  // Auto-correct event name in checkout-tickets URL
  if (to.name === 'checkout-tickets' && to.params.id) {
    const rawId = to.params.id as string;
    const isClosedEvent = /^[0-9a-f]{96}$/.test(rawId);

    // Closed events (hex IDs) don't use event name in URL
    if (isClosedEvent) {
      next();
      return;
    }

    const generalStore = useGeneralStore();
    const checkoutStore = useCheckoutStore();
    const cart = useCartStore();
    const parsedId = /^\d+$/.test(rawId) ? parseInt(rawId, 10) : null;

    // Try to find the event name from general store, checkout store, or cart items
    let eventName: string | undefined;
    if (parsedId !== null) {
      const event = generalStore.events().find(e => e.id === parsedId);
      if (event) eventName = event.info1;
    }
    if (!eventName && checkoutStore.event?.info1) {
      eventName = checkoutStore.event.info1;
    }
    if (!eventName) {
      const cartItem = cart.items.find(item => item.id == rawId);
      if (cartItem) eventName = cartItem.name;
    }

    if (eventName) {
      const sluggedName = slugify(eventName);
      if (to.params.name !== sluggedName) {
        next({
          name: 'checkout-tickets',
          params: { ...to.params, name: sluggedName },
          query: to.query,
          replace: true,
        });
        return;
      }
    }
    next();
    return;
  }

  // Add event name to URL if missing
  if (to.name === 'event-detail' && to.params.id && !to.params.name) {
    const generalStore = useGeneralStore();
    const rawId = to.params.id as string;
    const parsedId = /^\d+$/.test(rawId) ? parseInt(rawId, 10) : null;
    const event = parsedId !== null ? generalStore.events().find(event => event.id === parsedId) : undefined;

    if (event) {
      const eventName = slugify(event.info1);

      // Redirect to the same route with the event name added.
      // replace: true only when the user landed directly on /event/:id via the address bar (from.name === null).
      // In that case the nameless URL is the live browser entry and must be replaced to prevent a back-button
      // loop (/event/:id → guard → /event/:id/name → back → /event/:id → loop).
      // For in-SPA navigations the guard aborts the /event/:id navigation before it is ever committed to
      // history, so replace is not needed — the originating page (e.g. frontpage) stays in the history stack.
      next({
        name: to.name as string,
        params: {
          ...to.params,
          name: eventName,
        },
        replace: from.name === null,
      });
    } else {
      next(); // Proceed if the event is not found
    }
  } else {
    next();
  }
});

export default router;
